import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Container, Typography, Box, CircularProgress, Alert, TextField, Button, Paper
} from '@mui/material';
import { shuffleArray } from '../utils/quizUtils';

const QuizSetup = () => {
    const location = useLocation();
    const navigate = useNavigate();
    // Destructure the state passed from the previous page
    const { selectedFiles, searchResults } = location.state || {};

    const [allQuestions, setAllQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [numQuestions, setNumQuestions] = useState(10);
    const [timeLimit, setTimeLimit] = useState(10); // in minutes

    useEffect(() => {
        const loadQuestions = async () => {
            // Case 1: The user came from the search page
            if (searchResults) {
                setAllQuestions(searchResults);
                setNumQuestions(Math.min(10, searchResults.length));
                setLoading(false);
            // Case 2: The user came from the homepage or clicked "Retake Quiz"
            } else if (selectedFiles?.length > 0) {
                try {
                    const promises = selectedFiles.map(file => axios.get(`/quizzes/${file}`));
                    const responses = await Promise.all(promises);
                    const questions = responses.flatMap(res => res.data);
                    setAllQuestions(questions);
                    setNumQuestions(Math.min(10, questions.length));
                    setLoading(false);
                } catch (err) {
                    setError('Failed to load question data.');
                    setLoading(false);
                }
            } else {
                // If the user navigated here directly, send them back home
                navigate('/');
            }
        };
        loadQuestions();
    }, [selectedFiles, searchResults, navigate]);

    const handleStartQuiz = () => {
        // Ensure numQuestions is a valid number before starting
        const validNumQuestions = Math.min(Math.max(1, parseInt(numQuestions) || 1), allQuestions.length);
        
        const shuffled = shuffleArray([...allQuestions]);
        const quizQuestions = shuffled.slice(0, validNumQuestions);

        // Generate a dynamic title based on the source
        let quizTitle = 'Quiz'; 
        if (searchResults) {
            quizTitle = 'Quiz from Search Results';
        } else if (selectedFiles && selectedFiles.length > 0) {
            quizTitle = selectedFiles
                .map(filename => filename.replace('.json', ''))
                .join(', ');
        }

        // Navigate to the main quiz page with all the necessary data
        navigate('/quiz', {
            state: {
                questions: quizQuestions,
                timeLimitMinutes: timeLimit,
                title: quizTitle,
                // Pass the source files so they can be saved for the "Retake" feature.
                // Search-based quizzes cannot be retaken, so we pass null.
                sourceFiles: searchResults ? null : selectedFiles,
            }
        });
    };
    
    // --- Render Logic ---
    if (loading) return <Container sx={{ textAlign: 'center', mt: 4 }}><CircularProgress /></Container>;
    if (error) return <Container sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;
    // Prevent rendering the controls if there are no questions to avoid errors
    if (allQuestions.length === 0) return null;

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Typography variant="h4" align="center" gutterBottom>Configure Your Quiz</Typography>
            <Box p={3} component={Paper} elevation={3}>
                <Typography gutterBottom>
                    Total Available Questions: {allQuestions.length}
                </Typography>
                
                <TextField
                    fullWidth
                    label="Number of Questions"
                    type="number"
                    value={numQuestions}
                    onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val)) {
                            // Allow typing, but don't exceed max
                            if (val > allQuestions.length) {
                                setNumQuestions(allQuestions.length);
                            } else {
                                setNumQuestions(val);
                            }
                        } else {
                            setNumQuestions(''); // Allow clearing the field
                        }
                    }}
                    onBlur={() => {
                        // Ensure valid range on blur
                        let val = parseInt(numQuestions, 10);
                        if (isNaN(val) || val < 1) val = 1;
                        if (val > allQuestions.length) val = allQuestions.length;
                        setNumQuestions(val);
                    }}
                    inputProps={{ min: 1, max: allQuestions.length }}
                    sx={{ mt: 2, mb: 3 }}
                />

                <TextField
                    fullWidth
                    label="Time Limit (minutes)"
                    type="number"
                    value={timeLimit}
                    onChange={(e) => setTimeLimit(Math.max(1, parseInt(e.target.value, 10)) || 1)}
                    inputProps={{ min: 1 }}
                    sx={{ mb: 4 }}
                />
                <Button fullWidth variant="contained" size="large" onClick={handleStartQuiz}>
                    Start Quiz
                </Button>
            </Box>
        </Container>
    );
};

export default QuizSetup;
